import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert
} from '@mui/material'
import { GAME_TYPE_LABELS } from './GameTypeBadge'
import unifiedApi from '../../utils/unifiedApi'

// SchedulePlaceholderForm — create or edit a TBD vs TBD placeholder.
// Decision E: matches the look of the standard "create game" pattern, simplified.
const SchedulePlaceholderForm = ({ open, onClose, onSaved, leagueId, placeholder, fields = [], venues = [] }) => {
  const isEdit = !!placeholder
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [venueId, setVenueId] = useState('')
  const [fieldId, setFieldId] = useState('')
  const [homeLabel, setHomeLabel] = useState('TBD')
  const [awayLabel, setAwayLabel] = useState('TBD')
  const [gameType, setGameType] = useState(2) // Friendly default
  const [isPublished, setIsPublished] = useState(true)
  const [description, setDescription] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  // Decision C: info banner shown above form
  const infoMessage = 'Placeholders are TBD vs TBD entries that appear on the public schedule. When teams are decided, convert the placeholder into a real game.'

  useEffect(() => {
    if (!open) return
    if (placeholder) {
      // Backend returns dates already in tenant timezone as wall-clock ISO strings.
      // Slice the ISO string to feed datetime-local input directly, preserving the
      // wall-clock value without round-tripping through UTC.
      const toLocal = (d) => {
        if (!d) return ''
        if (typeof d === 'string') return d.slice(0, 16)
        // Fallback for Date objects: format using local components (avoids UTC shift).
        const dt = d
        const pad = (n) => String(n).padStart(2, '0')
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`
      }
      setStart(toLocal(placeholder.start))
      setEnd(toLocal(placeholder.end))
      setVenueId(placeholder.venueId || '')
      setFieldId(placeholder.fieldId || '')
      setHomeLabel(placeholder.homeLabel || 'TBD')
      setAwayLabel(placeholder.awayLabel || 'TBD')
      setGameType(placeholder.gameType ?? 2)
      setIsPublished(placeholder.isPublished ?? true)
      setDescription(placeholder.description || '')
    } else {
      setStart('')
      setEnd('')
      setVenueId('')
      setFieldId('')
      setHomeLabel('TBD')
      setAwayLabel('TBD')
      setGameType(2)
      setIsPublished(true)
      setDescription('')
    }
    setError(null)
  }, [open, placeholder])

  const filteredFields = useMemo(() => {
    if (!venueId) return fields
    return fields.filter(f => f.venueId === venueId || f.venueId === Number(venueId))
  }, [fields, venueId])

  const handleSave = async () => {
    if (!start) {
      setError('Start time is required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      // The backend treats incoming dates as tenant-local DateTime values and converts
      // them to UTC. Send the wall-clock string from the datetime-local input directly
      // — do not convert via Date#toISOString (which would emit a UTC Z value and
      // cause a double timezone shift on the server).
      const toNullableNumber = (v) => {
        if (v === '' || v === null || v === undefined) return null
        const n = Number(v)
        return Number.isFinite(n) ? n : null
      }
      const payload = {
        leagueId,
        start,
        end: end || null,
        venueId: toNullableNumber(venueId),
        fieldId: toNullableNumber(fieldId),
        homeLabel: homeLabel?.trim() || 'TBD',
        awayLabel: awayLabel?.trim() || 'TBD',
        gameType: Number(gameType),
        isPublished,
        description: description || null
      }
      let saved
      if (isEdit) {
        await unifiedApi.updateSchedulePlaceholder(placeholder.id, payload)
        saved = { ...placeholder, ...payload }
      } else {
        saved = await unifiedApi.createSchedulePlaceholder(payload)
      }
      if (onSaved) onSaved(saved)
      onClose()
    } catch (e) {
      setError(e?.message || 'Failed to save placeholder.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit Schedule Placeholder' : 'Add Schedule Placeholder'}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>{infoMessage}</Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Start"
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
          <TextField
            label="End (optional)"
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Venue (optional)"
            value={venueId}
            onChange={(e) => { setVenueId(e.target.value); setFieldId('') }}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {venues.map(v => (
              <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Field (optional)"
            value={fieldId}
            onChange={(e) => setFieldId(e.target.value)}
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {filteredFields.map(f => (
              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Home Label"
              value={homeLabel}
              onChange={(e) => setHomeLabel(e.target.value)}
              fullWidth
              helperText="Shown in place of the home team (e.g., TBD, Winner of A1)"
            />
            <TextField
              label="Away Label"
              value={awayLabel}
              onChange={(e) => setAwayLabel(e.target.value)}
              fullWidth
              helperText="Shown in place of the away team"
            />
          </Stack>
          <TextField
            select
            label="Game Type"
            value={gameType}
            onChange={(e) => setGameType(e.target.value)}
            fullWidth
          >
            {Object.entries(GAME_TYPE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />}
            label="Visible on public schedule"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SchedulePlaceholderForm